import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import * as React from 'react';
import styled from 'styled-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Table as MuiTable, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, makeStyles, createStyles, Checkbox, useTheme, capitalize } from '@material-ui/core';
import { useOverflow } from 'use-overflow'; // @ts-ignore

import { getBackgroundColor } from 'background-color-recursive';
import Icon from '../Icon';
import Spinner from '../Spinner';
import { TableActions } from './TableActions';
import { SearchBar } from './SearchBar';
import { ColumnControlPopoverList } from './ColumnControlPopoverList';
import { GroupRow } from './GroupRow';
import { Row } from './Row';
import { useEditableRows, useCustomColumns, usePagination, useSearch, useSort, useGroupedData, useMultiSelect } from './hooks';
import { getComparator, stableSort, stableSearch } from './utils';
import makeClass from 'classnames';
import { useEmphasis } from '../utils';
import Typography from '../Typography';
import { FilterPopover } from './FilterPopover';
import { filterToFields, operatorCharacterMap } from './FilterPopover/utils';
import Chip from '../Chip';
import { Link } from '..';
var useStyles = makeStyles(function (theme) {
  return createStyles({
    tableHeadCell: {
      textTransform: 'uppercase',
      fontSize: '12px !important',
      cursor: 'pointer',
      position: 'relative'
    },
    tableHeadCellNotInteractive: {
      cursor: 'default'
    },
    fixedTableHead: {
      wordBreak: 'break-word'
    },
    selectedCountBanner: {
      paddingLeft: theme.spacing(2),
      borderTop: "1px solid ".concat(theme.palette.divider)
    },
    filterChipsBanner: {
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(2),
      paddingBottom: '0',
      paddingLeft: theme.spacing(2),
      borderBottom: "1px solid ".concat(theme.palette.divider),
      display: 'flex',
      backgroundColor: theme.palette.background.default
    },
    filterChipsContainer: {
      flex: 1,
      overflowX: 'auto',
      whiteSpace: 'nowrap',
      '& div': {
        marginRight: theme.spacing(1),
        height: '20px'
      },
      paddingBottom: theme.spacing(1)
    },
    filterClearContainer: {
      paddingLeft: theme.spacing(1)
    }
  });
}); // TODO: https://philipwalton.github.io/polyfill/demos/position-sticky/

var StyledWrapper = styled.div.withConfig({
  displayName: "Table__StyledWrapper",
  componentId: "qam08h-0"
})(["width:100%;position:relative;"]);

var getBaseOverflowStyles = function getBaseOverflowStyles($hasOverflow, $overflowColor) {
  return "\n  background-color: ".concat($overflowColor, ";\n  &::before {\n    content: ' ';\n    display: ").concat($hasOverflow ? 'block' : 'none', ";\n    position: absolute;\n    height: 100%;\n    top: 0;\n    width: 80px;\n    left: 0;\n    margin-left: -80px;\n    background: linear-gradient(\n      to right,\n      rgba(255, 255, 255, 0) 0%,\n      ").concat($overflowColor, " 100%\n    );\n    pointer-events: none;\n  }\n");
};

var StyledFilterClearContainer = styled.div.withConfig({
  displayName: "Table__StyledFilterClearContainer",
  componentId: "qam08h-1"
})(["position:sticky;padding-left:1rem;", ""], function (_ref) {
  var $hasOverflow = _ref.$hasOverflow,
      $overflowColor = _ref.$overflowColor;
  return getBaseOverflowStyles($hasOverflow, $overflowColor);
});
export var StyledStickyCell = styled(TableCell).withConfig({
  displayName: "Table__StyledStickyCell",
  componentId: "qam08h-2"
})(["position:sticky;z-index:1;right:0px;padding:0px !important;z-index:1;", ""], function (_ref2) {
  var $hasOverflow = _ref2.$hasOverflow,
      $overflowColor = _ref2.$overflowColor;
  return getBaseOverflowStyles($hasOverflow, $overflowColor);
});
var StyledHeaderControlWrapper = styled.div.withConfig({
  displayName: "Table__StyledHeaderControlWrapper",
  componentId: "qam08h-3"
})(["display:flex;align-items:stretch;justify-content:flex-end;&:not(:empty){border-bottom:1px solid ", ";}"], function (_ref3) {
  var $divider = _ref3.$divider;
  return $divider;
});
var TableOpacity = styled.div.withConfig({
  displayName: "Table__TableOpacity",
  componentId: "qam08h-4"
})(["opacity:", ";"], function (_ref4) {
  var $loading = _ref4.$loading;
  return $loading ? 0.24 : 1;
});
var CenteredSpinner = styled.div.withConfig({
  displayName: "Table__CenteredSpinner",
  componentId: "qam08h-5"
})(["position:absolute;top:0;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;"]);
var LabelInfoTypography = styled(Typography).withConfig({
  displayName: "Table__LabelInfoTypography",
  componentId: "qam08h-6"
})(["&&{margin-left:0.5rem;}"]);
var LabelInfoIcon = styled(Icon).withConfig({
  displayName: "Table__LabelInfoIcon",
  componentId: "qam08h-7"
})(["&&{font-size:1rem;transform:translateY(3px);}"]);

/**
 * Check to see if a RowActionType is a GetActionData.
 */
export var isGetActionData = function isGetActionData(input) {
  return input.dsGetActionData !== undefined;
};
/**
 * Define a custom edit action for a row.
 */

export var FilterRelation;

(function (FilterRelation) {
  FilterRelation["AND"] = "AND";
  FilterRelation["OR"] = "OR";
})(FilterRelation || (FilterRelation = {}));

export var SortOrder;

(function (SortOrder) {
  SortOrder["ASC"] = "ASC";
  SortOrder["DESC"] = "DESC";
})(SortOrder || (SortOrder = {}));

var StyledStickyHeaderCell = styled(TableCell).withConfig({
  displayName: "Table__StyledStickyHeaderCell",
  componentId: "qam08h-8"
})(["&&{position:sticky;}&:empty{padding:0;}width:", "px;right:0;background-color:", ";z-index:1;", ""], function (_ref5) {
  var $actionCount = _ref5.$actionCount;
  return $actionCount * 40;
}, function (_ref6) {
  var $overflowColor = _ref6.$overflowColor;
  return $overflowColor;
}, function (_ref7) {
  var $hasOverflow = _ref7.$hasOverflow,
      $overflowColor = _ref7.$overflowColor;
  return getBaseOverflowStyles($hasOverflow, $overflowColor);
});
var StyledMuiTable = styled(MuiTable).withConfig({
  displayName: "Table__StyledMuiTable",
  componentId: "qam08h-9"
})(["table-layout:", ";"], function (_ref8) {
  var $fixed = _ref8.$fixed;
  return $fixed ? 'fixed' : 'auto';
});
var StyledColumnResizer = styled.button.withConfig({
  displayName: "Table__StyledColumnResizer",
  componentId: "qam08h-10"
})(["cursor:col-resize;border:none;background:none;color:currentcolor;display:flex;align-items:center;position:absolute;right:-16px;width:32px;top:0;bottom:0;margin:0;z-index:1;padding:0;&:focus{outline:none;}&:focus::after,&:hover::after{background-color:", ";width:2px;}&::after{background-color:", ";content:'';width:1px;height:calc(100% - ", ");position:absolute;top:", ";left:16px;}"], function (_ref9) {
  var $emphasis = _ref9.$emphasis;
  return $emphasis.medium;
}, function (_ref10) {
  var $emphasis = _ref10.$emphasis;
  return $emphasis.low;
}, function (_ref11) {
  var $dense = _ref11.$dense;
  return $dense ? '8px' : '16px';
}, function (_ref12) {
  var $dense = _ref12.$dense;
  return $dense ? '8px' : '16px';
});
var StyledColumnContainer = styled.div.withConfig({
  displayName: "Table__StyledColumnContainer",
  componentId: "qam08h-11"
})(["display:flex;"]);
var StyledColumnLabel = styled.span.withConfig({
  displayName: "Table__StyledColumnLabel",
  componentId: "qam08h-12"
})(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:", ";max-width:", ";"], function (_ref13) {
  var $color = _ref13.$color;
  return $color;
}, function (_ref14) {
  var active = _ref14.active;
  return active ? 'calc(100% + 26px)' : '100%';
});
var StyledTableSortLabel = styled(TableSortLabel).withConfig({
  displayName: "Table__StyledTableSortLabel",
  componentId: "qam08h-13"
})(["line-height:16px;width:", ";"], function (_ref15) {
  var active = _ref15.active;
  return active ? 'auto' : 0;
});
var StyledTableActionsContainer = styled.div.withConfig({
  displayName: "Table__StyledTableActionsContainer",
  componentId: "qam08h-14"
})(["border-left:", ";padding-left:", ";display:flex;align-items:center;"], function (_ref16) {
  var $notEmpty = _ref16.$notEmpty,
      $color = _ref16.$color;
  return $notEmpty ? "1px solid ".concat($color) : 'none';
}, function (_ref17) {
  var $notEmpty = _ref17.$notEmpty;
  return $notEmpty ? '8px' : 0;
});

var Table = function Table(_ref18) {
  var _chipsFilter$conditio2, _chipsFilter$conditio3, _chipsFilter$conditio4;

  var _ref18$dense = _ref18.dense,
      dense = _ref18$dense === void 0 ? false : _ref18$dense,
      e2e = _ref18.e2e,
      _ref18$fixed = _ref18.fixed,
      fixed = _ref18$fixed === void 0 ? false : _ref18$fixed,
      _ref18$rows = _ref18.rows,
      rows = _ref18$rows === void 0 ? [] : _ref18$rows,
      selectedRows = _ref18.selectedRows,
      _ref18$uniqueKey = _ref18.uniqueKey,
      uniqueKey = _ref18$uniqueKey === void 0 ? '' : _ref18$uniqueKey,
      groupByKey = _ref18.groupByKey,
      nestedDataKey = _ref18.nestedDataKey,
      _ref18$columns = _ref18.columns,
      inputColumns = _ref18$columns === void 0 ? [] : _ref18$columns,
      tableActions = _ref18.tableActions,
      _ref18$rowActions = _ref18.rowActions,
      rowActions = _ref18$rowActions === void 0 ? [] : _ref18$rowActions,
      searchable = _ref18.searchable,
      _ref18$searchPlacehol = _ref18.searchPlaceholder,
      searchPlaceholder = _ref18$searchPlacehol === void 0 ? 'Type here to filter results...' : _ref18$searchPlacehol,
      orderable = _ref18.orderable,
      filterableFields = _ref18.filterableFields,
      initialFilter = _ref18.initialFilter,
      dsOnFilter = _ref18.dsOnFilter,
      showFilterChips = _ref18.showFilterChips,
      hidePagination = _ref18.hidePagination,
      loading = _ref18.loading,
      dsOnPage = _ref18.dsOnPage,
      dsIsRowSelectable = _ref18.dsIsRowSelectable,
      dsIsCellEditable = _ref18.dsIsCellEditable,
      dsRenderMasterDetail = _ref18.dsRenderMasterDetail,
      dsOnEditChange = _ref18.dsOnEditChange,
      dsOnSelectionChange = _ref18.dsOnSelectionChange,
      dsOnSearchChange = _ref18.dsOnSearchChange,
      dsOnColumnsChange = _ref18.dsOnColumnsChange,
      dsOnColumnControlPopoverClick = _ref18.dsOnColumnControlPopoverClick,
      maxHeight = _ref18.maxHeight,
      _ref18$resizableColum = _ref18.resizableColumns,
      resizableColumns = _ref18$resizableColum === void 0 ? false : _ref18$resizableColum,
      inputRowsPerPage = _ref18.rowsPerPage,
      _ref18$rowsPerPageOpt = _ref18.rowsPerPageOptions,
      rowsPerPageOptions = _ref18$rowsPerPageOpt === void 0 ? [5, 10, 15, 20, 30, 50] : _ref18$rowsPerPageOpt,
      hideTotalRows = _ref18.hideTotalRows,
      _ref18$showSelectAll = _ref18.showSelectAll,
      showSelectAll = _ref18$showSelectAll === void 0 ? true : _ref18$showSelectAll,
      delegatedEditableRow = _ref18.delegatedEditableRow,
      dsCustomRowEditAction = _ref18.dsCustomRowEditAction,
      _ref18$sortable = _ref18.sortable,
      sortable = _ref18$sortable === void 0 ? true : _ref18$sortable,
      initialSort = _ref18.initialSort,
      dsOnSort = _ref18.dsOnSort,
      _ref18$sortStrategy = _ref18.sortStrategy,
      sortStrategy = _ref18$sortStrategy === void 0 ? 'client' : _ref18$sortStrategy;
  var classes = useStyles();
  var emphasis = useEmphasis();
  var tableContainerRef = React.useRef(null);
  var chipsOverflowContainerRef = React.useRef(null); // @ts-ignore

  var _useOverflow = useOverflow(tableContainerRef),
      refXScrollEnd = _useOverflow.refXScrollEnd,
      refXOverflowing = _useOverflow.refXOverflowing;

  var _useOverflow2 = useOverflow(chipsOverflowContainerRef),
      chipsXScrollEnd = _useOverflow2.refXScrollEnd,
      chipsXOverflowing = _useOverflow2.refXOverflowing;

  var _React$useState = React.useState(),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      overflowColor = _React$useState2[0],
      setOverflowColor = _React$useState2[1];

  var _React$useState3 = React.useState(-1),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      columnLabelTooltipIndex = _React$useState4[0],
      setColumnLabelTooltipIndex = _React$useState4[1];

  var hasActions = !!(rowActions === null || rowActions === void 0 ? void 0 : rowActions.length);
  var hasSelection = !!dsOnSelectionChange;
  var isEditable = !!dsOnEditChange;
  var hasNestedRows = !!nestedDataKey;
  var hasOverflow = fixed ? false : refXOverflowing && !refXScrollEnd;
  var chipsHasOverflow = fixed ? false : chipsXOverflowing && !chipsXScrollEnd;
  var theme = useTheme();
  var filterChipBackgroundColor = theme.palette.background.default; // @ts-ignore

  var nestedRowsMap = React.useMemo(function () {
    if (!nestedDataKey) return {};
    var nestedRowsMap = rows.reduce(function (acc, row) {
      var parentId = row[nestedDataKey];

      if (parentId) {
        // @ts-ignore
        acc[parentId] = [row].concat(acc[parentId] || []);
      }

      return acc;
    }, {});
    return nestedRowsMap;
  }, [rows, nestedDataKey]); // Editable row data

  var _useEditableRows = useEditableRows({
    uniqueKey: uniqueKey,
    rows: rows,
    dsOnEditChange: dsOnEditChange,
    delegatedEditableRow: delegatedEditableRow
  }),
      editableRow = _useEditableRows.editableRow,
      handleToggleRowEdit = _useEditableRows.handleToggleRowEdit,
      handleInputChange = _useEditableRows.handleInputChange; // Custom Columns


  var _useCustomColumns = useCustomColumns({
    inputColumns: inputColumns,
    dsOnColumnsChange: dsOnColumnsChange
  }),
      columns = _useCustomColumns.columns,
      handleColumnsChange = _useCustomColumns.handleColumnsChange; // Pagination


  var _usePagination = usePagination({
    // if we're hiding pagination, we show the entire collection at once
    inputRowsPerPage: hidePagination ? rows.length : inputRowsPerPage,
    dsOnPage: dsOnPage
  }),
      page = _usePagination.page,
      rowsPerPage = _usePagination.rowsPerPage,
      pageStart = _usePagination.pageStart,
      pageEnd = _usePagination.pageEnd,
      handleChangePage = _usePagination.handleChangePage,
      handleChangeRowsPerPage = _usePagination.handleChangeRowsPerPage,
      handleSetRowsPerPage = _usePagination.handleSetRowsPerPage;

  var resetPagination = function resetPagination() {
    if (page !== 0) {
      handleChangePage(0);
    }
  }; // Update pagination state if rows per page input changes


  React.useEffect(function () {
    if (!hidePagination && !!inputRowsPerPage) {
      handleSetRowsPerPage(inputRowsPerPage);
    }
  }, [inputRowsPerPage, hidePagination]); // Search

  var _useSearch = useSearch({
    page: page,
    handleChangePage: handleChangePage,
    dsOnSearchChange: dsOnSearchChange
  }),
      search = _useSearch.search,
      handleSearch = _useSearch.handleSearch; // Sort


  var _useSort = useSort({
    dsOnSort: dsOnSort,
    initialSort: initialSort,
    sortStrategy: sortStrategy
  }),
      order = _useSort.order,
      orderBy = _useSort.orderBy,
      handleSortClick = _useSort.handleSortClick; // Display Data


  var searchedAndSortedRows = React.useMemo(function () {
    var output = stableSearch(rows, search);

    if (orderBy && sortStrategy === 'client') {
      output = stableSort(output, // @ts-ignore TODO fix type
      getComparator(order, orderBy));
    }

    return output;
  }, [rows, search, order, orderBy]); // Grouped Data

  var _useGroupedData = useGroupedData({
    groupByKey: groupByKey,
    searchedAndSortedRows: searchedAndSortedRows,
    nestedDataKey: nestedDataKey
  }),
      collapsedGroups = _useGroupedData.collapsedGroups,
      groupedRows = _useGroupedData.groupedRows,
      _onToggleHeaderRow = _useGroupedData.onToggleHeaderRow;

  var rowsInView = React.useMemo(function () {
    var collection = _toConsumableArray(groupedRows.collection);

    if (!collection.length) return [];
    var out = [];
    var i = pageStart;

    while (out.length < rowsPerPage && collection[i]) {
      var item = collection[i]; // omit nested rows

      if (!item.hasOwnProperty(nestedDataKey)) {
        out.push(item);
      }

      i++;
    }

    return out;
  }, [groupedRows, pageStart, rowsPerPage]); // Multi-Select

  var _useMultiSelect = useMultiSelect({
    uniqueKey: uniqueKey,
    selectedRows: selectedRows,
    rowsInView: rowsInView,
    nestedRowsMap: nestedRowsMap,
    dsOnSelectionChange: dsOnSelectionChange
  }),
      selectedMap = _useMultiSelect.selectedMap,
      handleSelectAllClick = _useMultiSelect.handleSelectAllClick,
      handleCheckboxClick = _useMultiSelect.handleCheckboxClick,
      isSelected = _useMultiSelect.isSelected,
      handleClearSelections = _useMultiSelect.handleClearSelections; // Column Resize


  var resizeRefs = React.useRef([]);
  var activeColumn = React.useRef(null);
  var columnRefs = React.useRef({});
  var columnMap = columns.reduce(function (prevValue, currValue) {
    return _objectSpread(_objectSpread({}, prevValue), {}, _defineProperty({}, currValue.field, currValue));
  }, {});

  var getColumnWidth = function getColumnWidth(columnName) {
    var column = columnMap[columnName];

    if (column === null || column === void 0 ? void 0 : column.width) {
      return column.width;
    }

    var node = columnRefs.current[columnName];

    if (node) {
      var _node$getBoundingClie = node.getBoundingClientRect(),
          width = _node$getBoundingClie.width;

      return width;
    }
  };

  var selectedRowsCount = Object.values(selectedMap).length;
  var totalRows = rows.length; // TODO: move to TableRows component along with all the rest of the GroupRow / Row rendering logic
  // -- track the start idx to avoid rendering collapsed groups

  var startIdx = 0;
  var colSpan = columns.length + (hasSelection ? 1 : 0) + (hasActions || isEditable ? 1 : 0) + (hasNestedRows ? 1 : 0);
  var actionCount = isEditable ? rowActions.length + 1 : rowActions.length;
  var hasTableActions = Boolean((tableActions === null || tableActions === void 0 ? void 0 : tableActions.length) || orderable || (filterableFields === null || filterableFields === void 0 ? void 0 : filterableFields.length));
  /* Filtering */

  var _React$useState5 = React.useState({}),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      fields = _React$useState6[0],
      setFields = _React$useState6[1];

  var _React$useState7 = React.useState(undefined),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      filter = _React$useState8[0],
      setFilter = _React$useState8[1];

  var chipsFilter = filter || initialFilter;
  React.useEffect(function () {
    if (filterableFields) {
      setFields(filterToFields(filterableFields, initialFilter));
    }
  }, [filterableFields]);
  React.useEffect(function () {
    if (dsOnFilter && filter) {
      dsOnFilter({
        filter: filter
      });
      resetPagination();
      handleClearSelections();
    }
  }, [filter]);

  var resetPageOnSort = function resetPageOnSort(property) {
    handleSortClick(property);
    resetPagination();
    handleClearSelections();
  };

  var removeFilter = function removeFilter() {
    setFilter({});
    setFields(filterToFields(filterableFields));
  };

  var removeFilterByField = function removeFilterByField(chip) {
    var _chipsFilter$conditio;

    if ((chipsFilter === null || chipsFilter === void 0 ? void 0 : (_chipsFilter$conditio = chipsFilter.conditions) === null || _chipsFilter$conditio === void 0 ? void 0 : _chipsFilter$conditio.length) > 1) {
      var modifiedFilter = _objectSpread(_objectSpread({}, chipsFilter), {}, {
        conditions: chipsFilter === null || chipsFilter === void 0 ? void 0 : chipsFilter.conditions.filter(function (condition) {
          return condition.field !== chip.field;
        })
      });

      setFilter(modifiedFilter);
      setFields(filterToFields(filterableFields, modifiedFilter));
    } else {
      removeFilter();
    }
  };

  return /*#__PURE__*/React.createElement(StyledWrapper, _extends({}, e2e && {
    'data-e2e': "".concat(e2e, "-table")
  }, {
    ref: function ref(wrapper) {
      if (wrapper) {
        var color = getBackgroundColor(wrapper);
        color && setOverflowColor(color);
      }
    }
  }), /*#__PURE__*/React.createElement(TableOpacity, {
    $loading: loading
  }, /*#__PURE__*/React.createElement(StyledHeaderControlWrapper, _extends({}, e2e && {
    'data-e2e': "".concat(e2e, "-table-controls")
  }, {
    $divider: theme.palette.divider
  }), searchable && /*#__PURE__*/React.createElement(SearchBar, {
    e2e: e2e,
    search: search,
    searchPlaceholder: searchPlaceholder,
    onSearch: handleSearch
  }), hasTableActions && /*#__PURE__*/React.createElement(StyledTableActionsContainer, {
    $color: emphasis.contextHigh,
    $notEmpty: hasTableActions
  }, /*#__PURE__*/React.createElement(TableActions, {
    e2e: e2e,
    tableActions: tableActions
  }), (filterableFields === null || filterableFields === void 0 ? void 0 : filterableFields.length) && /*#__PURE__*/React.createElement(FilterPopover, {
    e2e: e2e,
    hasActions: Boolean(tableActions === null || tableActions === void 0 ? void 0 : tableActions.length),
    filterableFields: filterableFields,
    fields: fields,
    setFilter: setFilter,
    setFields: setFields,
    removeFilter: removeFilter
  }), orderable && /*#__PURE__*/React.createElement(DndProvider, {
    backend: HTML5Backend
  }, /*#__PURE__*/React.createElement(ColumnControlPopoverList, {
    e2e: e2e,
    hasActions: Boolean(tableActions === null || tableActions === void 0 ? void 0 : tableActions.length),
    columns: columns,
    onColumnControlPopoverClick: dsOnColumnControlPopoverClick,
    onColumnsChange: handleColumnsChange
  })))), showFilterChips && /*#__PURE__*/React.createElement("div", {
    className: classes.filterChipsBanner
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.filterChipsContainer,
    ref: chipsOverflowContainerRef
  }, (chipsFilter === null || chipsFilter === void 0 ? void 0 : (_chipsFilter$conditio2 = chipsFilter.conditions) === null || _chipsFilter$conditio2 === void 0 ? void 0 : _chipsFilter$conditio2.length) > 0 && (chipsFilter === null || chipsFilter === void 0 ? void 0 : (_chipsFilter$conditio3 = chipsFilter.conditions) === null || _chipsFilter$conditio3 === void 0 ? void 0 : _chipsFilter$conditio3.map(function (chip) {
    var _fields$chip$field;

    return /*#__PURE__*/React.createElement(Chip, {
      e2e: "".concat(chip.field, "-").concat(chip.operator, "-").concat(chip.value),
      key: "".concat(chip.field, "-").concat(chip.operator, "-").concat(chip.value),
      state: "default",
      uppercase: false,
      label: "".concat(((_fields$chip$field = fields[chip.field]) === null || _fields$chip$field === void 0 ? void 0 : _fields$chip$field.label) || capitalize(chip.field), " ").concat(operatorCharacterMap[chip.operator], " ").concat(chip.value),
      dismissible: true,
      dsOnDelete: function dsOnDelete() {
        return removeFilterByField(chip);
      }
    });
  })) || 'No Filters Applied'), (chipsFilter === null || chipsFilter === void 0 ? void 0 : (_chipsFilter$conditio4 = chipsFilter.conditions) === null || _chipsFilter$conditio4 === void 0 ? void 0 : _chipsFilter$conditio4.length) > 0 && /*#__PURE__*/React.createElement(StyledFilterClearContainer, _extends({}, e2e && {
    'data-e2e': "".concat(e2e, "-chip-bar-clear-filter")
  }, {
    onClick: removeFilter,
    $overflowColor: filterChipBackgroundColor,
    $hasOverflow: chipsHasOverflow
  }), /*#__PURE__*/React.createElement(Link, null, "Clear Filters"))), selectedRowsCount > 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.selectedCountBanner
  }, selectedRowsCount, " rows selected"), /*#__PURE__*/React.createElement(TableContainer, {
    ref: tableContainerRef,
    style: {
      maxHeight: maxHeight
    }
  }, /*#__PURE__*/React.createElement(StyledMuiTable, {
    stickyHeader: !!maxHeight,
    $fixed: fixed
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, hasNestedRows && /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell,
    size: dense ? 'small' : 'medium'
  }), hasSelection && /*#__PURE__*/React.createElement(TableCell, {
    size: dense ? 'small' : 'medium',
    padding: "checkbox",
    className: classes.tableHeadCell,
    style: {
      width: 'auto'
    }
  }, showSelectAll && /*#__PURE__*/React.createElement(Checkbox, {
    color: "primary",
    indeterminate: selectedRowsCount > 0 && selectedRowsCount < totalRows,
    checked: rowsInView.every(function (r) {
      return !!selectedMap[r[uniqueKey]];
    }) && !!rowsInView.length,
    disabled: !rowsInView.length,
    onChange: handleSelectAllClick,
    inputProps: _objectSpread(_objectSpread({}, e2e && {
      'data-e2e': "".concat(e2e, "-table-head-checkbox")
    }), {
      'aria-label': 'select all'
    })
  })), columns.map(function (column, columnIndex) {
    var _column$sortable, _makeClass;

    if (column.hidden) return null;
    var active = orderBy === column.field;
    var columnSortable = sortable && ((_column$sortable = column.sortable) !== null && _column$sortable !== void 0 ? _column$sortable : true);
    return /*#__PURE__*/React.createElement(TableCell, _extends({
      key: "table-head-table-cell-".concat(columnIndex)
    }, e2e && {
      'data-e2e': "".concat(e2e, "-table-head-table-cell-").concat(column.field)
    }, {
      className: makeClass(classes.tableHeadCell, (_makeClass = {}, _defineProperty(_makeClass, classes.fixedTableHead, fixed), _defineProperty(_makeClass, classes.tableHeadCellNotInteractive, !columnSortable), _makeClass)),
      size: dense ? 'small' : 'medium',
      ref: function ref(thNode) {
        columnRefs.current[column.field] = thNode;
      }
    }, columnSortable && {
      tabIndex: 0,
      onClick: function onClick() {
        return resetPageOnSort(column.field);
      },
      onKeyDown: function onKeyDown(e) {
        var isSpace = e.keyCode === 32;
        var isEnter = e.keyCode === 13;

        if (isSpace || isEnter) {
          resetPageOnSort(column.field);
        }
      }
    }, {
      onMouseEnter: function onMouseEnter() {
        return setColumnLabelTooltipIndex(columnIndex);
      },
      onMouseLeave: function onMouseLeave() {
        return setColumnLabelTooltipIndex(-1);
      }
    }), /*#__PURE__*/React.createElement(StyledColumnContainer, null, /*#__PURE__*/React.createElement(StyledColumnLabel, {
      $color: active ? emphasis.high : emphasis.medium,
      active: active
    }, column.label || column.field, column.labelTooltip && /*#__PURE__*/React.createElement(LabelInfoTypography, {
      body: /*#__PURE__*/React.createElement(LabelInfoIcon, {
        body: "help_outline"
      }),
      component: "span",
      tooltip: column.labelTooltip,
      tooltipOpen: columnLabelTooltipIndex === columnIndex
    })), columnSortable && /*#__PURE__*/React.createElement(StyledTableSortLabel, {
      tabIndex: -1,
      active: active,
      direction: active ? order : 'asc'
    })), resizableColumns && /*#__PURE__*/React.createElement(StyledColumnResizer, {
      $dense: dense,
      $emphasis: emphasis,
      ref: function ref(elem) {
        if (elem) {
          resizeRefs.current.push(elem);
        }
      },
      onClick: function onClick(e) {
        // prevent sorting
        e.stopPropagation();
      },
      onMouseDownCapture: function onMouseDownCapture(e) {
        var _e$currentTarget, _e$currentTarget$pare;

        activeColumn.current = {
          field: column.field,
          initPageX: Number(e.pageX),
          initWidth: (_e$currentTarget = e.currentTarget) === null || _e$currentTarget === void 0 ? void 0 : (_e$currentTarget$pare = _e$currentTarget.parentElement) === null || _e$currentTarget$pare === void 0 ? void 0 : _e$currentTarget$pare.offsetWidth
        };

        var handleMouseMove = function handleMouseMove(e) {
          var _activeColumn$current;

          if (((_activeColumn$current = activeColumn.current) === null || _activeColumn$current === void 0 ? void 0 : _activeColumn$current.field) === column.field) {
            var width = activeColumn.current.initWidth;

            if (!width) {
              return;
            }

            var diffX = e.pageX - activeColumn.current.initPageX; // Minimum width of 80px

            var newWidth = Math.max(width + diffX, 80);
            [columnRefs.current[column.field]].concat(_toConsumableArray(Array.from(tableContainerRef.current.querySelectorAll("[data-column=\"".concat(column.field, "\"]"))))).forEach(function (cell) {
              // Imperatively control the width while dragging for maximal performance.
              cell.style.width = "".concat(newWidth, "px");
              cell.style.minWidth = "".concat(newWidth, "px");
              cell.style.maxWidth = "".concat(newWidth, "px");
            });
          }
        };

        var handleMouseUp = function handleMouseUp(e) {
          window.document.removeEventListener('mousemove', handleMouseMove);
          window.document.removeEventListener('mouseup', handleMouseUp);

          var clonedColumns = _toConsumableArray(columns).map(function (c) {
            return _objectSpread({}, c);
          });

          var newColumn = clonedColumns.find(function (col) {
            var _activeColumn$current2;

            return col.field === ((_activeColumn$current2 = activeColumn.current) === null || _activeColumn$current2 === void 0 ? void 0 : _activeColumn$current2.field);
          });

          if (newColumn && (activeColumn === null || activeColumn === void 0 ? void 0 : activeColumn.current)) {
            var _activeColumn$current3, _activeColumn$current4;

            var width = (_activeColumn$current3 = activeColumn.current) === null || _activeColumn$current3 === void 0 ? void 0 : _activeColumn$current3.initWidth;

            if (!width) {
              return;
            }

            var diffX = e.pageX - ((_activeColumn$current4 = activeColumn.current) === null || _activeColumn$current4 === void 0 ? void 0 : _activeColumn$current4.initPageX); // Minimum width of 80px

            var newWidth = Math.max(width + diffX, 80);
            newColumn.width = newWidth;
            activeColumn.current = null;
            dsOnColumnsChange === null || dsOnColumnsChange === void 0 ? void 0 : dsOnColumnsChange(clonedColumns);
          }
        };

        window.document.addEventListener('mousemove', handleMouseMove);
        window.document.addEventListener('mouseup', handleMouseUp);
      },
      onKeyDown: function onKeyDown(e) {
        if (e.key === 'Tab') {
          return;
        }

        e.preventDefault();
        var width = getColumnWidth(column.field);

        if (width) {
          var newColumns = _toConsumableArray(columns);

          var widthChange = function () {
            if (e.key === 'ArrowLeft') {
              return -5;
            }

            if (e.key === 'ArrowRight') {
              return 5;
            }

            return 0;
          }(); // min width is 80px


          var newWidth = Math.max(width + widthChange, 80);
          newColumns[columnIndex].width = newWidth;
          dsOnColumnsChange === null || dsOnColumnsChange === void 0 ? void 0 : dsOnColumnsChange(newColumns);
        }
      }
    }));
  }), /*#__PURE__*/React.createElement(StyledStickyHeaderCell, {
    $actionCount: actionCount,
    className: classes.tableHeadCell,
    $overflowColor: overflowColor,
    $hasOverflow: hasOverflow
  }))), /*#__PURE__*/React.createElement(TableBody, null, Object.entries(groupedRows.map).map(function (_ref19) {
    var _ref20 = _slicedToArray(_ref19, 2),
        groupKey = _ref20[0],
        children = _ref20[1];

    var isItemGroupExpanded = !collapsedGroups[groupKey];
    var lowerBound = startIdx;

    if (pageStart > startIdx + children.length || pageEnd < startIdx) {
      startIdx += children.length;
      return null;
    } else if (isItemGroupExpanded) {
      startIdx += children.length;
    }

    var isPastLowerBound = pageStart > lowerBound;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: "table-body-row-group-fragment-".concat(groupKey)
    }, !!groupKey && /*#__PURE__*/React.createElement(GroupRow, _extends({}, e2e && {
      e2e: "".concat(e2e, "-group-").concat(groupKey)
    }, {
      colSpan: colSpan,
      expanded: isItemGroupExpanded,
      disabled: isPastLowerBound,
      hidden: isPastLowerBound && !isItemGroupExpanded,
      onToggleHeaderRow: function onToggleHeaderRow() {
        return _onToggleHeaderRow(groupKey);
      }
    }), groupKey, isPastLowerBound && '  (continued from previous page)'), rowsInView.map(function (row, rowIndex) {
      if (!isItemGroupExpanded) return null; // @ts-ignore TODO fix row indexes

      if (!!groupKey && row[groupByKey] !== groupKey) {
        return null;
      }

      return /*#__PURE__*/React.createElement(Row, _extends({
        key: "table-body-row-".concat(groupKey, "-").concat(rowIndex, "-").concat(row[uniqueKey])
      }, e2e && {
        e2e: "".concat(e2e, "-table-body-row-").concat(row[uniqueKey])
      }, {
        dense: dense,
        columns: columns,
        hasOverflow: hasOverflow,
        overflowColor: overflowColor,
        colSpan: colSpan,
        row: row,
        rows: rows,
        nestedRowsMap: nestedRowsMap,
        editableRow: editableRow,
        uniqueKey: uniqueKey,
        nestedDataKey: nestedDataKey,
        rowActions: rowActions,
        isEditable: isEditable,
        isSelected: isSelected,
        isSelectable: hasSelection,
        dsRenderMasterDetail: dsRenderMasterDetail,
        dsIsRowSelectable: dsIsRowSelectable,
        dsIsCellEditable: dsIsCellEditable,
        onCheckboxClick: handleCheckboxClick,
        onToggleRowEdit: handleToggleRowEdit,
        onInputChange: handleInputChange,
        dsCustomRowEditAction: dsCustomRowEditAction
      }));
    }));
  })))), groupedRows.collection.length > 0 && !hidePagination && /*#__PURE__*/React.createElement(TablePagination, {
    size: dense ? 'small' : 'medium',
    nextIconButtonProps: _objectSpread({}, e2e && {
      'data-e2e': "".concat(e2e, "-table-pagination-next-button")
    }),
    backIconButtonProps: _objectSpread({}, e2e && {
      'data-e2e': "".concat(e2e, "-table-pagination-back-button")
    }),
    SelectProps: _objectSpread({}, e2e && {
      'data-e2e': "".concat(e2e, "-table-pagination-select")
    }),
    rowsPerPageOptions: rowsPerPageOptions,
    component: "div",
    count: groupedRows.collection.length,
    rowsPerPage: rowsPerPage,
    page: page,
    onChangePage: function onChangePage(_, page) {
      return handleChangePage(page);
    },
    onChangeRowsPerPage: handleChangeRowsPerPage,
    labelDisplayedRows: function labelDisplayedRows(_ref21) {
      var from = _ref21.from,
          to = _ref21.to,
          count = _ref21.count;
      var range = "".concat(from, " - ").concat(to);
      return hideTotalRows ? range : "".concat(range, " | ").concat(count);
    },
    labelRowsPerPage: null
  })), loading && /*#__PURE__*/React.createElement(CenteredSpinner, null, /*#__PURE__*/React.createElement(Spinner, null)));
};

export default Table;

try {
  StyledStickyCell.__docgenInfo = {
    description: '',
    displayName: 'StyledStickyCell',
    methods: [],
    props: {
      classes: {
        defaultValue: null,
        description: 'Override or extend the styles applied to the component.',
        name: 'classes',
        required: false,
        type: {
          name: 'Partial<ClassNameMap<TableCellClassKey>>'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      defaultChecked: {
        defaultValue: null,
        description: '',
        name: 'defaultChecked',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      defaultValue: {
        defaultValue: null,
        description: '',
        name: 'defaultValue',
        required: false,
        type: {
          name: 'string | number | readonly string[]'
        }
      },
      suppressContentEditableWarning: {
        defaultValue: null,
        description: '',
        name: 'suppressContentEditableWarning',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      suppressHydrationWarning: {
        defaultValue: null,
        description: '',
        name: 'suppressHydrationWarning',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      accessKey: {
        defaultValue: null,
        description: '',
        name: 'accessKey',
        required: false,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        required: false,
        type: {
          name: 'string'
        }
      },
      contentEditable: {
        defaultValue: null,
        description: '',
        name: 'contentEditable',
        required: false,
        type: {
          name: '"inherit" | Booleanish'
        }
      },
      contextMenu: {
        defaultValue: null,
        description: '',
        name: 'contextMenu',
        required: false,
        type: {
          name: 'string'
        }
      },
      dir: {
        defaultValue: null,
        description: '',
        name: 'dir',
        required: false,
        type: {
          name: 'string'
        }
      },
      draggable: {
        defaultValue: null,
        description: '',
        name: 'draggable',
        required: false,
        type: {
          name: 'Booleanish'
        }
      },
      hidden: {
        defaultValue: null,
        description: '',
        name: 'hidden',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        required: false,
        type: {
          name: 'string'
        }
      },
      lang: {
        defaultValue: null,
        description: '',
        name: 'lang',
        required: false,
        type: {
          name: 'string'
        }
      },
      placeholder: {
        defaultValue: null,
        description: '',
        name: 'placeholder',
        required: false,
        type: {
          name: 'string'
        }
      },
      slot: {
        defaultValue: null,
        description: '',
        name: 'slot',
        required: false,
        type: {
          name: 'string'
        }
      },
      spellCheck: {
        defaultValue: null,
        description: '',
        name: 'spellCheck',
        required: false,
        type: {
          name: 'Booleanish'
        }
      },
      style: {
        defaultValue: null,
        description: '',
        name: 'style',
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      tabIndex: {
        defaultValue: null,
        description: '',
        name: 'tabIndex',
        required: false,
        type: {
          name: 'number'
        }
      },
      title: {
        defaultValue: null,
        description: '',
        name: 'title',
        required: false,
        type: {
          name: 'string'
        }
      },
      translate: {
        defaultValue: null,
        description: '',
        name: 'translate',
        required: false,
        type: {
          name: 'enum',
          raw: '"yes" | "no"',
          value: [{
            value: '"yes"'
          }, {
            value: '"no"'
          }]
        }
      },
      radioGroup: {
        defaultValue: null,
        description: '',
        name: 'radioGroup',
        required: false,
        type: {
          name: 'string'
        }
      },
      role: {
        defaultValue: null,
        description: '',
        name: 'role',
        required: false,
        type: {
          name: 'string'
        }
      },
      about: {
        defaultValue: null,
        description: '',
        name: 'about',
        required: false,
        type: {
          name: 'string'
        }
      },
      datatype: {
        defaultValue: null,
        description: '',
        name: 'datatype',
        required: false,
        type: {
          name: 'string'
        }
      },
      inlist: {
        defaultValue: null,
        description: '',
        name: 'inlist',
        required: false,
        type: {
          name: 'any'
        }
      },
      prefix: {
        defaultValue: null,
        description: '',
        name: 'prefix',
        required: false,
        type: {
          name: 'string'
        }
      },
      property: {
        defaultValue: null,
        description: '',
        name: 'property',
        required: false,
        type: {
          name: 'string'
        }
      },
      resource: {
        defaultValue: null,
        description: '',
        name: 'resource',
        required: false,
        type: {
          name: 'string'
        }
      },
      typeof: {
        defaultValue: null,
        description: '',
        name: 'typeof',
        required: false,
        type: {
          name: 'string'
        }
      },
      vocab: {
        defaultValue: null,
        description: '',
        name: 'vocab',
        required: false,
        type: {
          name: 'string'
        }
      },
      autoCapitalize: {
        defaultValue: null,
        description: '',
        name: 'autoCapitalize',
        required: false,
        type: {
          name: 'string'
        }
      },
      autoCorrect: {
        defaultValue: null,
        description: '',
        name: 'autoCorrect',
        required: false,
        type: {
          name: 'string'
        }
      },
      autoSave: {
        defaultValue: null,
        description: '',
        name: 'autoSave',
        required: false,
        type: {
          name: 'string'
        }
      },
      color: {
        defaultValue: null,
        description: '',
        name: 'color',
        required: false,
        type: {
          name: 'string'
        }
      },
      itemProp: {
        defaultValue: null,
        description: '',
        name: 'itemProp',
        required: false,
        type: {
          name: 'string'
        }
      },
      itemScope: {
        defaultValue: null,
        description: '',
        name: 'itemScope',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      itemType: {
        defaultValue: null,
        description: '',
        name: 'itemType',
        required: false,
        type: {
          name: 'string'
        }
      },
      itemID: {
        defaultValue: null,
        description: '',
        name: 'itemID',
        required: false,
        type: {
          name: 'string'
        }
      },
      itemRef: {
        defaultValue: null,
        description: '',
        name: 'itemRef',
        required: false,
        type: {
          name: 'string'
        }
      },
      results: {
        defaultValue: null,
        description: '',
        name: 'results',
        required: false,
        type: {
          name: 'number'
        }
      },
      security: {
        defaultValue: null,
        description: '',
        name: 'security',
        required: false,
        type: {
          name: 'string'
        }
      },
      unselectable: {
        defaultValue: null,
        description: '',
        name: 'unselectable',
        required: false,
        type: {
          name: 'enum',
          raw: '"on" | "off"',
          value: [{
            value: '"on"'
          }, {
            value: '"off"'
          }]
        }
      },
      inputMode: {
        defaultValue: null,
        description: 'Hints at the type of data that might be entered by the user while editing the element or its contents\n' + '@see https ://html.spec.whatwg.org/multipage/interaction.html#input-modalities:-the-inputmode-attribute',
        name: 'inputMode',
        required: false,
        type: {
          name: 'enum',
          raw: '"text" | "none" | "tel" | "url" | "email" | "numeric" | "decimal" | "search"',
          value: [{
            value: '"text"'
          }, {
            value: '"none"'
          }, {
            value: '"tel"'
          }, {
            value: '"url"'
          }, {
            value: '"email"'
          }, {
            value: '"numeric"'
          }, {
            value: '"decimal"'
          }, {
            value: '"search"'
          }]
        }
      },
      is: {
        defaultValue: null,
        description: 'Specify that a standard HTML element should behave like a defined custom built-in element\n' + '@see https ://html.spec.whatwg.org/multipage/custom-elements.html#attr-is',
        name: 'is',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-activedescendant': {
        defaultValue: null,
        description: 'Identifies the currently active element when DOM focus is on a composite widget, textbox, group, or application.',
        name: 'aria-activedescendant',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-atomic': {
        defaultValue: null,
        description: 'Indicates whether assistive technologies will present all, or only parts of, the changed region based on the change notifications defined by the aria-relevant attribute.',
        name: 'aria-atomic',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-autocomplete': {
        defaultValue: null,
        description: "Indicates whether inputting text could trigger display of one or more predictions of the user's intended value for an input and specifies how predictions would be\n" + 'presented if they are made.',
        name: 'aria-autocomplete',
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "list" | "inline" | "both"',
          value: [{
            value: '"none"'
          }, {
            value: '"list"'
          }, {
            value: '"inline"'
          }, {
            value: '"both"'
          }]
        }
      },
      'aria-busy': {
        defaultValue: null,
        description: 'Indicates an element is being modified and that assistive technologies MAY want to wait until the modifications are complete before exposing them to the user.',
        name: 'aria-busy',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-checked': {
        defaultValue: null,
        description: 'Indicates the current "checked" state of checkboxes, radio buttons, and other widgets.\n' + '@see aria-pressed\n' + '@see aria-selected.',
        name: 'aria-checked',
        required: false,
        type: {
          name: 'boolean | "true" | "false" | "mixed"'
        }
      },
      'aria-colcount': {
        defaultValue: null,
        description: 'Defines the total number of columns in a table, grid, or treegrid.\n' + '@see aria-colindex.',
        name: 'aria-colcount',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-colindex': {
        defaultValue: null,
        description: "Defines an element's column index or position with respect to the total number of columns within a table, grid, or treegrid.\n" + '@see aria-colcount\n' + '@see aria-colspan.',
        name: 'aria-colindex',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-colspan': {
        defaultValue: null,
        description: 'Defines the number of columns spanned by a cell or gridcell within a table, grid, or treegrid.\n' + '@see aria-colindex\n' + '@see aria-rowspan.',
        name: 'aria-colspan',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-controls': {
        defaultValue: null,
        description: 'Identifies the element (or elements) whose contents or presence are controlled by the current element.\n' + '@see aria-owns.',
        name: 'aria-controls',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-current': {
        defaultValue: null,
        description: 'Indicates the element that represents the current item within a container or set of related elements.',
        name: 'aria-current',
        required: false,
        type: {
          name: 'boolean | "time" | "true" | "false" | "page" | "step" | "location" | "date"'
        }
      },
      'aria-describedby': {
        defaultValue: null,
        description: 'Identifies the element (or elements) that describes the object.\n' + '@see aria-labelledby',
        name: 'aria-describedby',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-details': {
        defaultValue: null,
        description: 'Identifies the element that provides a detailed, extended description for the object.\n' + '@see aria-describedby.',
        name: 'aria-details',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-disabled': {
        defaultValue: null,
        description: 'Indicates that the element is perceivable but disabled, so it is not editable or otherwise operable.\n' + '@see aria-hidden\n' + '@see aria-readonly.',
        name: 'aria-disabled',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-dropeffect': {
        defaultValue: null,
        description: 'Indicates what functions can be performed when a dragged object is released on the drop target.\n' + '@deprecated in ARIA 1.1',
        name: 'aria-dropeffect',
        required: false,
        type: {
          name: 'enum',
          raw: '"link" | "none" | "move" | "copy" | "execute" | "popup"',
          value: [{
            value: '"link"'
          }, {
            value: '"none"'
          }, {
            value: '"move"'
          }, {
            value: '"copy"'
          }, {
            value: '"execute"'
          }, {
            value: '"popup"'
          }]
        }
      },
      'aria-errormessage': {
        defaultValue: null,
        description: 'Identifies the element that provides an error message for the object.\n' + '@see aria-invalid\n' + '@see aria-describedby.',
        name: 'aria-errormessage',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-expanded': {
        defaultValue: null,
        description: 'Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed.',
        name: 'aria-expanded',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-flowto': {
        defaultValue: null,
        description: "Identifies the next element (or elements) in an alternate reading order of content which, at the user's discretion,\n" + 'allows assistive technology to override the general default of reading in document source order.',
        name: 'aria-flowto',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-grabbed': {
        defaultValue: null,
        description: "Indicates an element's \"grabbed\" state in a drag-and-drop operation.\n" + '@deprecated in ARIA 1.1',
        name: 'aria-grabbed',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-haspopup': {
        defaultValue: null,
        description: 'Indicates the availability and type of interactive popup element, such as menu or dialog, that can be triggered by an element.',
        name: 'aria-haspopup',
        required: false,
        type: {
          name: 'boolean | "dialog" | "menu" | "true" | "false" | "listbox" | "tree" | "grid"'
        }
      },
      'aria-hidden': {
        defaultValue: null,
        description: 'Indicates whether the element is exposed to an accessibility API.\n' + '@see aria-disabled.',
        name: 'aria-hidden',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-invalid': {
        defaultValue: null,
        description: 'Indicates the entered value does not conform to the format expected by the application.\n' + '@see aria-errormessage.',
        name: 'aria-invalid',
        required: false,
        type: {
          name: 'boolean | "true" | "false" | "grammar" | "spelling"'
        }
      },
      'aria-keyshortcuts': {
        defaultValue: null,
        description: 'Indicates keyboard shortcuts that an author has implemented to activate or give focus to an element.',
        name: 'aria-keyshortcuts',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-labelledby': {
        defaultValue: null,
        description: 'Identifies the element (or elements) that labels the current element.\n' + '@see aria-describedby.',
        name: 'aria-labelledby',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-level': {
        defaultValue: null,
        description: 'Defines the hierarchical level of an element within a structure.',
        name: 'aria-level',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-live': {
        defaultValue: null,
        description: 'Indicates that an element will be updated, and describes the types of updates the user agents, assistive technologies, and user can expect from the live region.',
        name: 'aria-live',
        required: false,
        type: {
          name: 'enum',
          raw: '"off" | "assertive" | "polite"',
          value: [{
            value: '"off"'
          }, {
            value: '"assertive"'
          }, {
            value: '"polite"'
          }]
        }
      },
      'aria-modal': {
        defaultValue: null,
        description: 'Indicates whether an element is modal when displayed.',
        name: 'aria-modal',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-multiline': {
        defaultValue: null,
        description: 'Indicates whether a text box accepts multiple lines of input or only a single line.',
        name: 'aria-multiline',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-multiselectable': {
        defaultValue: null,
        description: 'Indicates that the user may select more than one item from the current selectable descendants.',
        name: 'aria-multiselectable',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-orientation': {
        defaultValue: null,
        description: "Indicates whether the element's orientation is horizontal, vertical, or unknown/ambiguous.",
        name: 'aria-orientation',
        required: false,
        type: {
          name: 'enum',
          raw: '"horizontal" | "vertical"',
          value: [{
            value: '"horizontal"'
          }, {
            value: '"vertical"'
          }]
        }
      },
      'aria-owns': {
        defaultValue: null,
        description: 'Identifies an element (or elements) in order to define a visual, functional, or contextual parent/child relationship\n' + 'between DOM elements where the DOM hierarchy cannot be used to represent the relationship.\n' + '@see aria-controls.',
        name: 'aria-owns',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-placeholder': {
        defaultValue: null,
        description: 'Defines a short hint (a word or short phrase) intended to aid the user with data entry when the control has no value.\n' + 'A hint could be a sample value or a brief description of the expected format.',
        name: 'aria-placeholder',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-posinset': {
        defaultValue: null,
        description: "Defines an element's number or position in the current set of listitems or treeitems. Not required if all elements in the set are present in the DOM.\n" + '@see aria-setsize.',
        name: 'aria-posinset',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-pressed': {
        defaultValue: null,
        description: 'Indicates the current "pressed" state of toggle buttons.\n' + '@see aria-checked\n' + '@see aria-selected.',
        name: 'aria-pressed',
        required: false,
        type: {
          name: 'boolean | "true" | "false" | "mixed"'
        }
      },
      'aria-readonly': {
        defaultValue: null,
        description: 'Indicates that the element is not editable, but is otherwise operable.\n' + '@see aria-disabled.',
        name: 'aria-readonly',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-relevant': {
        defaultValue: null,
        description: 'Indicates what notifications the user agent will trigger when the accessibility tree within a live region is modified.\n' + '@see aria-atomic.',
        name: 'aria-relevant',
        required: false,
        type: {
          name: 'enum',
          raw: '"text" | "additions" | "additions removals" | "additions text" | "all" | "removals" | "removals additions" | "removals text" | "text additions" | "text removals"',
          value: [{
            value: '"text"'
          }, {
            value: '"additions"'
          }, {
            value: '"additions removals"'
          }, {
            value: '"additions text"'
          }, {
            value: '"all"'
          }, {
            value: '"removals"'
          }, {
            value: '"removals additions"'
          }, {
            value: '"removals text"'
          }, {
            value: '"text additions"'
          }, {
            value: '"text removals"'
          }]
        }
      },
      'aria-required': {
        defaultValue: null,
        description: 'Indicates that user input is required on the element before a form may be submitted.',
        name: 'aria-required',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-roledescription': {
        defaultValue: null,
        description: 'Defines a human-readable, author-localized description for the role of an element.',
        name: 'aria-roledescription',
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-rowcount': {
        defaultValue: null,
        description: 'Defines the total number of rows in a table, grid, or treegrid.\n' + '@see aria-rowindex.',
        name: 'aria-rowcount',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-rowindex': {
        defaultValue: null,
        description: "Defines an element's row index or position with respect to the total number of rows within a table, grid, or treegrid.\n" + '@see aria-rowcount\n' + '@see aria-rowspan.',
        name: 'aria-rowindex',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-rowspan': {
        defaultValue: null,
        description: 'Defines the number of rows spanned by a cell or gridcell within a table, grid, or treegrid.\n' + '@see aria-rowindex\n' + '@see aria-colspan.',
        name: 'aria-rowspan',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-selected': {
        defaultValue: null,
        description: 'Indicates the current "selected" state of various widgets.\n' + '@see aria-checked\n' + '@see aria-pressed.',
        name: 'aria-selected',
        required: false,
        type: {
          name: 'boolean | "true" | "false"'
        }
      },
      'aria-setsize': {
        defaultValue: null,
        description: 'Defines the number of items in the current set of listitems or treeitems. Not required if all elements in the set are present in the DOM.\n' + '@see aria-posinset.',
        name: 'aria-setsize',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-sort': {
        defaultValue: null,
        description: 'Indicates if items in a table or grid are sorted in ascending or descending order.',
        name: 'aria-sort',
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "ascending" | "descending" | "other"',
          value: [{
            value: '"none"'
          }, {
            value: '"ascending"'
          }, {
            value: '"descending"'
          }, {
            value: '"other"'
          }]
        }
      },
      'aria-valuemax': {
        defaultValue: null,
        description: 'Defines the maximum allowed value for a range widget.',
        name: 'aria-valuemax',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-valuemin': {
        defaultValue: null,
        description: 'Defines the minimum allowed value for a range widget.',
        name: 'aria-valuemin',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-valuenow': {
        defaultValue: null,
        description: 'Defines the current value for a range widget.\n@see aria-valuetext.',
        name: 'aria-valuenow',
        required: false,
        type: {
          name: 'number'
        }
      },
      'aria-valuetext': {
        defaultValue: null,
        description: 'Defines the human readable text alternative of aria-valuenow for a range widget.',
        name: 'aria-valuetext',
        required: false,
        type: {
          name: 'string'
        }
      },
      children: {
        defaultValue: null,
        description: 'The table cell contents.',
        name: 'children',
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      dangerouslySetInnerHTML: {
        defaultValue: null,
        description: '',
        name: 'dangerouslySetInnerHTML',
        required: false,
        type: {
          name: '{ __html: string; }'
        }
      },
      onCopy: {
        defaultValue: null,
        description: '',
        name: 'onCopy',
        required: false,
        type: {
          name: 'ClipboardEventHandler<HTMLTableHeaderCellElement> & ClipboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCopyCapture: {
        defaultValue: null,
        description: '',
        name: 'onCopyCapture',
        required: false,
        type: {
          name: 'ClipboardEventHandler<HTMLTableHeaderCellElement> & ClipboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCut: {
        defaultValue: null,
        description: '',
        name: 'onCut',
        required: false,
        type: {
          name: 'ClipboardEventHandler<HTMLTableHeaderCellElement> & ClipboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCutCapture: {
        defaultValue: null,
        description: '',
        name: 'onCutCapture',
        required: false,
        type: {
          name: 'ClipboardEventHandler<HTMLTableHeaderCellElement> & ClipboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPaste: {
        defaultValue: null,
        description: '',
        name: 'onPaste',
        required: false,
        type: {
          name: 'ClipboardEventHandler<HTMLTableHeaderCellElement> & ClipboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPasteCapture: {
        defaultValue: null,
        description: '',
        name: 'onPasteCapture',
        required: false,
        type: {
          name: 'ClipboardEventHandler<HTMLTableHeaderCellElement> & ClipboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCompositionEnd: {
        defaultValue: null,
        description: '',
        name: 'onCompositionEnd',
        required: false,
        type: {
          name: 'CompositionEventHandler<HTMLTableHeaderCellElement> & CompositionEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCompositionEndCapture: {
        defaultValue: null,
        description: '',
        name: 'onCompositionEndCapture',
        required: false,
        type: {
          name: 'CompositionEventHandler<HTMLTableHeaderCellElement> & CompositionEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCompositionStart: {
        defaultValue: null,
        description: '',
        name: 'onCompositionStart',
        required: false,
        type: {
          name: 'CompositionEventHandler<HTMLTableHeaderCellElement> & CompositionEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCompositionStartCapture: {
        defaultValue: null,
        description: '',
        name: 'onCompositionStartCapture',
        required: false,
        type: {
          name: 'CompositionEventHandler<HTMLTableHeaderCellElement> & CompositionEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCompositionUpdate: {
        defaultValue: null,
        description: '',
        name: 'onCompositionUpdate',
        required: false,
        type: {
          name: 'CompositionEventHandler<HTMLTableHeaderCellElement> & CompositionEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCompositionUpdateCapture: {
        defaultValue: null,
        description: '',
        name: 'onCompositionUpdateCapture',
        required: false,
        type: {
          name: 'CompositionEventHandler<HTMLTableHeaderCellElement> & CompositionEventHandler<HTMLTableDataCellElement>'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLTableHeaderCellElement> & FocusEventHandler<HTMLTableDataCellElement>'
        }
      },
      onFocusCapture: {
        defaultValue: null,
        description: '',
        name: 'onFocusCapture',
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLTableHeaderCellElement> & FocusEventHandler<HTMLTableDataCellElement>'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLTableHeaderCellElement> & FocusEventHandler<HTMLTableDataCellElement>'
        }
      },
      onBlurCapture: {
        defaultValue: null,
        description: '',
        name: 'onBlurCapture',
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLTableHeaderCellElement> & FocusEventHandler<HTMLTableDataCellElement>'
        }
      },
      onChangeCapture: {
        defaultValue: null,
        description: '',
        name: 'onChangeCapture',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onBeforeInput: {
        defaultValue: null,
        description: '',
        name: 'onBeforeInput',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onBeforeInputCapture: {
        defaultValue: null,
        description: '',
        name: 'onBeforeInputCapture',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onInput: {
        defaultValue: null,
        description: '',
        name: 'onInput',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onInputCapture: {
        defaultValue: null,
        description: '',
        name: 'onInputCapture',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onReset: {
        defaultValue: null,
        description: '',
        name: 'onReset',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onResetCapture: {
        defaultValue: null,
        description: '',
        name: 'onResetCapture',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSubmit: {
        defaultValue: null,
        description: '',
        name: 'onSubmit',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSubmitCapture: {
        defaultValue: null,
        description: '',
        name: 'onSubmitCapture',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onInvalid: {
        defaultValue: null,
        description: '',
        name: 'onInvalid',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onInvalidCapture: {
        defaultValue: null,
        description: '',
        name: 'onInvalidCapture',
        required: false,
        type: {
          name: 'FormEventHandler<HTMLTableHeaderCellElement> & FormEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLoad: {
        defaultValue: null,
        description: '',
        name: 'onLoad',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLoadCapture: {
        defaultValue: null,
        description: '',
        name: 'onLoadCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onError: {
        defaultValue: null,
        description: '',
        name: 'onError',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onErrorCapture: {
        defaultValue: null,
        description: '',
        name: 'onErrorCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onKeyDown: {
        defaultValue: null,
        description: '',
        name: 'onKeyDown',
        required: false,
        type: {
          name: 'KeyboardEventHandler<HTMLTableHeaderCellElement> & KeyboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onKeyDownCapture: {
        defaultValue: null,
        description: '',
        name: 'onKeyDownCapture',
        required: false,
        type: {
          name: 'KeyboardEventHandler<HTMLTableHeaderCellElement> & KeyboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onKeyPress: {
        defaultValue: null,
        description: '',
        name: 'onKeyPress',
        required: false,
        type: {
          name: 'KeyboardEventHandler<HTMLTableHeaderCellElement> & KeyboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onKeyPressCapture: {
        defaultValue: null,
        description: '',
        name: 'onKeyPressCapture',
        required: false,
        type: {
          name: 'KeyboardEventHandler<HTMLTableHeaderCellElement> & KeyboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onKeyUp: {
        defaultValue: null,
        description: '',
        name: 'onKeyUp',
        required: false,
        type: {
          name: 'KeyboardEventHandler<HTMLTableHeaderCellElement> & KeyboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onKeyUpCapture: {
        defaultValue: null,
        description: '',
        name: 'onKeyUpCapture',
        required: false,
        type: {
          name: 'KeyboardEventHandler<HTMLTableHeaderCellElement> & KeyboardEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAbort: {
        defaultValue: null,
        description: '',
        name: 'onAbort',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAbortCapture: {
        defaultValue: null,
        description: '',
        name: 'onAbortCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCanPlay: {
        defaultValue: null,
        description: '',
        name: 'onCanPlay',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCanPlayCapture: {
        defaultValue: null,
        description: '',
        name: 'onCanPlayCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCanPlayThrough: {
        defaultValue: null,
        description: '',
        name: 'onCanPlayThrough',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onCanPlayThroughCapture: {
        defaultValue: null,
        description: '',
        name: 'onCanPlayThroughCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDurationChange: {
        defaultValue: null,
        description: '',
        name: 'onDurationChange',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDurationChangeCapture: {
        defaultValue: null,
        description: '',
        name: 'onDurationChangeCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onEmptied: {
        defaultValue: null,
        description: '',
        name: 'onEmptied',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onEmptiedCapture: {
        defaultValue: null,
        description: '',
        name: 'onEmptiedCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onEncrypted: {
        defaultValue: null,
        description: '',
        name: 'onEncrypted',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onEncryptedCapture: {
        defaultValue: null,
        description: '',
        name: 'onEncryptedCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onEnded: {
        defaultValue: null,
        description: '',
        name: 'onEnded',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onEndedCapture: {
        defaultValue: null,
        description: '',
        name: 'onEndedCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLoadedData: {
        defaultValue: null,
        description: '',
        name: 'onLoadedData',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLoadedDataCapture: {
        defaultValue: null,
        description: '',
        name: 'onLoadedDataCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLoadedMetadata: {
        defaultValue: null,
        description: '',
        name: 'onLoadedMetadata',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLoadedMetadataCapture: {
        defaultValue: null,
        description: '',
        name: 'onLoadedMetadataCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLoadStart: {
        defaultValue: null,
        description: '',
        name: 'onLoadStart',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLoadStartCapture: {
        defaultValue: null,
        description: '',
        name: 'onLoadStartCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPause: {
        defaultValue: null,
        description: '',
        name: 'onPause',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPauseCapture: {
        defaultValue: null,
        description: '',
        name: 'onPauseCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPlay: {
        defaultValue: null,
        description: '',
        name: 'onPlay',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPlayCapture: {
        defaultValue: null,
        description: '',
        name: 'onPlayCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPlaying: {
        defaultValue: null,
        description: '',
        name: 'onPlaying',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPlayingCapture: {
        defaultValue: null,
        description: '',
        name: 'onPlayingCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onProgress: {
        defaultValue: null,
        description: '',
        name: 'onProgress',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onProgressCapture: {
        defaultValue: null,
        description: '',
        name: 'onProgressCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onRateChange: {
        defaultValue: null,
        description: '',
        name: 'onRateChange',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onRateChangeCapture: {
        defaultValue: null,
        description: '',
        name: 'onRateChangeCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSeeked: {
        defaultValue: null,
        description: '',
        name: 'onSeeked',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSeekedCapture: {
        defaultValue: null,
        description: '',
        name: 'onSeekedCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSeeking: {
        defaultValue: null,
        description: '',
        name: 'onSeeking',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSeekingCapture: {
        defaultValue: null,
        description: '',
        name: 'onSeekingCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onStalled: {
        defaultValue: null,
        description: '',
        name: 'onStalled',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onStalledCapture: {
        defaultValue: null,
        description: '',
        name: 'onStalledCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSuspend: {
        defaultValue: null,
        description: '',
        name: 'onSuspend',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSuspendCapture: {
        defaultValue: null,
        description: '',
        name: 'onSuspendCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTimeUpdate: {
        defaultValue: null,
        description: '',
        name: 'onTimeUpdate',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTimeUpdateCapture: {
        defaultValue: null,
        description: '',
        name: 'onTimeUpdateCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onVolumeChange: {
        defaultValue: null,
        description: '',
        name: 'onVolumeChange',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onVolumeChangeCapture: {
        defaultValue: null,
        description: '',
        name: 'onVolumeChangeCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onWaiting: {
        defaultValue: null,
        description: '',
        name: 'onWaiting',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onWaitingCapture: {
        defaultValue: null,
        description: '',
        name: 'onWaitingCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAuxClick: {
        defaultValue: null,
        description: '',
        name: 'onAuxClick',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAuxClickCapture: {
        defaultValue: null,
        description: '',
        name: 'onAuxClickCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onClickCapture: {
        defaultValue: null,
        description: '',
        name: 'onClickCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onContextMenu: {
        defaultValue: null,
        description: '',
        name: 'onContextMenu',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onContextMenuCapture: {
        defaultValue: null,
        description: '',
        name: 'onContextMenuCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDoubleClick: {
        defaultValue: null,
        description: '',
        name: 'onDoubleClick',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDoubleClickCapture: {
        defaultValue: null,
        description: '',
        name: 'onDoubleClickCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDrag: {
        defaultValue: null,
        description: '',
        name: 'onDrag',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragCapture: {
        defaultValue: null,
        description: '',
        name: 'onDragCapture',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragEnd: {
        defaultValue: null,
        description: '',
        name: 'onDragEnd',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragEndCapture: {
        defaultValue: null,
        description: '',
        name: 'onDragEndCapture',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragEnter: {
        defaultValue: null,
        description: '',
        name: 'onDragEnter',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragEnterCapture: {
        defaultValue: null,
        description: '',
        name: 'onDragEnterCapture',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragExit: {
        defaultValue: null,
        description: '',
        name: 'onDragExit',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragExitCapture: {
        defaultValue: null,
        description: '',
        name: 'onDragExitCapture',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragLeave: {
        defaultValue: null,
        description: '',
        name: 'onDragLeave',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragLeaveCapture: {
        defaultValue: null,
        description: '',
        name: 'onDragLeaveCapture',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragOver: {
        defaultValue: null,
        description: '',
        name: 'onDragOver',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragOverCapture: {
        defaultValue: null,
        description: '',
        name: 'onDragOverCapture',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragStart: {
        defaultValue: null,
        description: '',
        name: 'onDragStart',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDragStartCapture: {
        defaultValue: null,
        description: '',
        name: 'onDragStartCapture',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDrop: {
        defaultValue: null,
        description: '',
        name: 'onDrop',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onDropCapture: {
        defaultValue: null,
        description: '',
        name: 'onDropCapture',
        required: false,
        type: {
          name: 'DragEventHandler<HTMLTableHeaderCellElement> & DragEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseDown: {
        defaultValue: null,
        description: '',
        name: 'onMouseDown',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseDownCapture: {
        defaultValue: null,
        description: '',
        name: 'onMouseDownCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseEnter: {
        defaultValue: null,
        description: '',
        name: 'onMouseEnter',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseLeave: {
        defaultValue: null,
        description: '',
        name: 'onMouseLeave',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseMove: {
        defaultValue: null,
        description: '',
        name: 'onMouseMove',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseMoveCapture: {
        defaultValue: null,
        description: '',
        name: 'onMouseMoveCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseOut: {
        defaultValue: null,
        description: '',
        name: 'onMouseOut',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseOutCapture: {
        defaultValue: null,
        description: '',
        name: 'onMouseOutCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseOver: {
        defaultValue: null,
        description: '',
        name: 'onMouseOver',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseOverCapture: {
        defaultValue: null,
        description: '',
        name: 'onMouseOverCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseUp: {
        defaultValue: null,
        description: '',
        name: 'onMouseUp',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onMouseUpCapture: {
        defaultValue: null,
        description: '',
        name: 'onMouseUpCapture',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLTableHeaderCellElement> & MouseEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSelect: {
        defaultValue: null,
        description: '',
        name: 'onSelect',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onSelectCapture: {
        defaultValue: null,
        description: '',
        name: 'onSelectCapture',
        required: false,
        type: {
          name: 'ReactEventHandler<HTMLTableHeaderCellElement> & ReactEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTouchCancel: {
        defaultValue: null,
        description: '',
        name: 'onTouchCancel',
        required: false,
        type: {
          name: 'TouchEventHandler<HTMLTableHeaderCellElement> & TouchEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTouchCancelCapture: {
        defaultValue: null,
        description: '',
        name: 'onTouchCancelCapture',
        required: false,
        type: {
          name: 'TouchEventHandler<HTMLTableHeaderCellElement> & TouchEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTouchEnd: {
        defaultValue: null,
        description: '',
        name: 'onTouchEnd',
        required: false,
        type: {
          name: 'TouchEventHandler<HTMLTableHeaderCellElement> & TouchEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTouchEndCapture: {
        defaultValue: null,
        description: '',
        name: 'onTouchEndCapture',
        required: false,
        type: {
          name: 'TouchEventHandler<HTMLTableHeaderCellElement> & TouchEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTouchMove: {
        defaultValue: null,
        description: '',
        name: 'onTouchMove',
        required: false,
        type: {
          name: 'TouchEventHandler<HTMLTableHeaderCellElement> & TouchEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTouchMoveCapture: {
        defaultValue: null,
        description: '',
        name: 'onTouchMoveCapture',
        required: false,
        type: {
          name: 'TouchEventHandler<HTMLTableHeaderCellElement> & TouchEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTouchStart: {
        defaultValue: null,
        description: '',
        name: 'onTouchStart',
        required: false,
        type: {
          name: 'TouchEventHandler<HTMLTableHeaderCellElement> & TouchEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTouchStartCapture: {
        defaultValue: null,
        description: '',
        name: 'onTouchStartCapture',
        required: false,
        type: {
          name: 'TouchEventHandler<HTMLTableHeaderCellElement> & TouchEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerDown: {
        defaultValue: null,
        description: '',
        name: 'onPointerDown',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerDownCapture: {
        defaultValue: null,
        description: '',
        name: 'onPointerDownCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerMove: {
        defaultValue: null,
        description: '',
        name: 'onPointerMove',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerMoveCapture: {
        defaultValue: null,
        description: '',
        name: 'onPointerMoveCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerUp: {
        defaultValue: null,
        description: '',
        name: 'onPointerUp',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerUpCapture: {
        defaultValue: null,
        description: '',
        name: 'onPointerUpCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerCancel: {
        defaultValue: null,
        description: '',
        name: 'onPointerCancel',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerCancelCapture: {
        defaultValue: null,
        description: '',
        name: 'onPointerCancelCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerEnter: {
        defaultValue: null,
        description: '',
        name: 'onPointerEnter',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerEnterCapture: {
        defaultValue: null,
        description: '',
        name: 'onPointerEnterCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerLeave: {
        defaultValue: null,
        description: '',
        name: 'onPointerLeave',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerLeaveCapture: {
        defaultValue: null,
        description: '',
        name: 'onPointerLeaveCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerOver: {
        defaultValue: null,
        description: '',
        name: 'onPointerOver',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerOverCapture: {
        defaultValue: null,
        description: '',
        name: 'onPointerOverCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerOut: {
        defaultValue: null,
        description: '',
        name: 'onPointerOut',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onPointerOutCapture: {
        defaultValue: null,
        description: '',
        name: 'onPointerOutCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onGotPointerCapture: {
        defaultValue: null,
        description: '',
        name: 'onGotPointerCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onGotPointerCaptureCapture: {
        defaultValue: null,
        description: '',
        name: 'onGotPointerCaptureCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLostPointerCapture: {
        defaultValue: null,
        description: '',
        name: 'onLostPointerCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onLostPointerCaptureCapture: {
        defaultValue: null,
        description: '',
        name: 'onLostPointerCaptureCapture',
        required: false,
        type: {
          name: 'PointerEventHandler<HTMLTableHeaderCellElement> & PointerEventHandler<HTMLTableDataCellElement>'
        }
      },
      onScroll: {
        defaultValue: null,
        description: '',
        name: 'onScroll',
        required: false,
        type: {
          name: 'UIEventHandler<HTMLTableHeaderCellElement> & UIEventHandler<HTMLTableDataCellElement>'
        }
      },
      onScrollCapture: {
        defaultValue: null,
        description: '',
        name: 'onScrollCapture',
        required: false,
        type: {
          name: 'UIEventHandler<HTMLTableHeaderCellElement> & UIEventHandler<HTMLTableDataCellElement>'
        }
      },
      onWheel: {
        defaultValue: null,
        description: '',
        name: 'onWheel',
        required: false,
        type: {
          name: 'WheelEventHandler<HTMLTableHeaderCellElement> & WheelEventHandler<HTMLTableDataCellElement>'
        }
      },
      onWheelCapture: {
        defaultValue: null,
        description: '',
        name: 'onWheelCapture',
        required: false,
        type: {
          name: 'WheelEventHandler<HTMLTableHeaderCellElement> & WheelEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAnimationStart: {
        defaultValue: null,
        description: '',
        name: 'onAnimationStart',
        required: false,
        type: {
          name: 'AnimationEventHandler<HTMLTableHeaderCellElement> & AnimationEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAnimationStartCapture: {
        defaultValue: null,
        description: '',
        name: 'onAnimationStartCapture',
        required: false,
        type: {
          name: 'AnimationEventHandler<HTMLTableHeaderCellElement> & AnimationEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAnimationEnd: {
        defaultValue: null,
        description: '',
        name: 'onAnimationEnd',
        required: false,
        type: {
          name: 'AnimationEventHandler<HTMLTableHeaderCellElement> & AnimationEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAnimationEndCapture: {
        defaultValue: null,
        description: '',
        name: 'onAnimationEndCapture',
        required: false,
        type: {
          name: 'AnimationEventHandler<HTMLTableHeaderCellElement> & AnimationEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAnimationIteration: {
        defaultValue: null,
        description: '',
        name: 'onAnimationIteration',
        required: false,
        type: {
          name: 'AnimationEventHandler<HTMLTableHeaderCellElement> & AnimationEventHandler<HTMLTableDataCellElement>'
        }
      },
      onAnimationIterationCapture: {
        defaultValue: null,
        description: '',
        name: 'onAnimationIterationCapture',
        required: false,
        type: {
          name: 'AnimationEventHandler<HTMLTableHeaderCellElement> & AnimationEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTransitionEnd: {
        defaultValue: null,
        description: '',
        name: 'onTransitionEnd',
        required: false,
        type: {
          name: 'TransitionEventHandler<HTMLTableHeaderCellElement> & TransitionEventHandler<HTMLTableDataCellElement>'
        }
      },
      onTransitionEndCapture: {
        defaultValue: null,
        description: '',
        name: 'onTransitionEndCapture',
        required: false,
        type: {
          name: 'TransitionEventHandler<HTMLTableHeaderCellElement> & TransitionEventHandler<HTMLTableDataCellElement>'
        }
      },
      component: {
        defaultValue: null,
        description: 'The component used for the root node.\n' + 'Either a string to use a HTML element or a component.',
        name: 'component',
        required: false,
        type: {
          name: 'ElementType<TableCellBaseProps>'
        }
      },
      variant: {
        defaultValue: null,
        description: 'Specify the cell type.\n' + 'By default, the TableHead, TableBody or TableFooter parent component set the value.',
        name: 'variant',
        required: false,
        type: {
          name: 'enum',
          raw: '"body" | "footer" | "head"',
          value: [{
            value: '"body"'
          }, {
            value: '"footer"'
          }, {
            value: '"head"'
          }]
        }
      },
      innerRef: {
        defaultValue: null,
        description: '',
        name: 'innerRef',
        required: false,
        type: {
          name: 'Ref<any>'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        required: false,
        type: {
          name: 'Ref<unknown>'
        }
      },
      size: {
        defaultValue: null,
        description: 'Specify the size of the cell.\n' + 'By default, the Table parent component set the value (`medium`).',
        name: 'size',
        required: false,
        type: {
          name: 'enum',
          raw: 'Size',
          value: [{
            value: '"small"'
          }, {
            value: '"medium"'
          }]
        }
      },
      align: {
        defaultValue: null,
        description: 'Set the text-align on the table cell content.\n' + '\n' + 'Monetary or generally number fields **should be right aligned** as that allows\n' + 'you to add them up quickly in your head without having to worry about decimals.',
        name: 'align',
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "left" | "right" | "inherit" | "justify"',
          value: [{
            value: '"center"'
          }, {
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"inherit"'
          }, {
            value: '"justify"'
          }]
        }
      },
      abbr: {
        defaultValue: null,
        description: '',
        name: 'abbr',
        required: false,
        type: {
          name: 'string'
        }
      },
      height: {
        defaultValue: null,
        description: '',
        name: 'height',
        required: false,
        type: {
          name: 'string | number'
        }
      },
      padding: {
        defaultValue: null,
        description: 'Sets the padding applied to the cell.\n' + 'By default, the Table parent component set the value (`default`).',
        name: 'padding',
        required: false,
        type: {
          name: 'enum',
          raw: 'Padding',
          value: [{
            value: '"default"'
          }, {
            value: '"none"'
          }, {
            value: '"checkbox"'
          }]
        }
      },
      width: {
        defaultValue: null,
        description: '',
        name: 'width',
        required: false,
        type: {
          name: 'string | number'
        }
      },
      colSpan: {
        defaultValue: null,
        description: '',
        name: 'colSpan',
        required: false,
        type: {
          name: 'number'
        }
      },
      headers: {
        defaultValue: null,
        description: '',
        name: 'headers',
        required: false,
        type: {
          name: 'string'
        }
      },
      rowSpan: {
        defaultValue: null,
        description: '',
        name: 'rowSpan',
        required: false,
        type: {
          name: 'number'
        }
      },
      scope: {
        defaultValue: null,
        description: 'Set scope attribute.',
        name: 'scope',
        required: false,
        type: {
          name: 'string'
        }
      },
      valign: {
        defaultValue: null,
        description: '',
        name: 'valign',
        required: false,
        type: {
          name: 'enum',
          raw: '"baseline" | "middle" | "top" | "bottom"',
          value: [{
            value: '"baseline"'
          }, {
            value: '"middle"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }]
        }
      },
      sortDirection: {
        defaultValue: null,
        description: 'Set aria-sort direction.',
        name: 'sortDirection',
        required: false,
        type: {
          name: 'SortDirection'
        }
      },
      '$hasOverflow': {
        defaultValue: null,
        description: '',
        name: '$hasOverflow',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      '$overflowColor': {
        defaultValue: null,
        description: '',
        name: '$overflowColor',
        required: false,
        type: {
          name: 'string'
        }
      },
      theme: {
        defaultValue: null,
        description: '',
        name: 'theme',
        required: false,
        type: {
          name: 'any'
        }
      },
      as: {
        defaultValue: null,
        description: '',
        name: 'as',
        required: false,
        type: {
          name: 'never'
        }
      },
      forwardedAs: {
        defaultValue: null,
        description: '',
        name: 'forwardedAs',
        required: false,
        type: {
          name: 'never'
        }
      }
    }
  };
} catch (e) {}

try {
  isGetActionData.__docgenInfo = {
    description: 'Check to see if a RowActionType is a GetActionData.',
    displayName: 'isGetActionData',
    methods: [],
    props: {
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        required: true,
        type: {
          name: '({ row }: { row: DataType; }) => void'
        }
      },
      icon: {
        defaultValue: null,
        description: '',
        name: 'icon',
        required: true,
        type: {
          name: 'any'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        required: false,
        type: {
          name: 'string'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      tooltip: {
        defaultValue: null,
        description: '',
        name: 'tooltip',
        required: false,
        type: {
          name: 'string'
        }
      },
      dsGetActionData: {
        defaultValue: null,
        description: '',
        name: 'dsGetActionData',
        required: true,
        type: {
          name: '(row: DataType) => RowAction'
        }
      }
    }
  };
} catch (e) {}

try {
  Table.__docgenInfo = {
    description: '',
    displayName: 'Table',
    methods: [],
    props: {
      e2e: {
        defaultValue: null,
        description: '',
        name: 'e2e',
        required: false,
        type: {
          name: 'string'
        }
      },
      tableActions: {
        defaultValue: null,
        description: '',
        name: 'tableActions',
        required: false,
        type: {
          name: 'TableActionType[]'
        }
      },
      rowActions: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'rowActions',
        required: false,
        type: {
          name: 'RowActionType[]'
        }
      },
      columns: {
        defaultValue: null,
        description: '',
        name: 'columns',
        required: true,
        type: {
          name: 'ColumnType[]'
        }
      },
      rows: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'rows',
        required: false,
        type: {
          name: 'DataType[]'
        }
      },
      selectedRows: {
        defaultValue: null,
        description: '',
        name: 'selectedRows',
        required: false,
        type: {
          name: '{ [key: string]: DataType; }'
        }
      },
      dense: {
        defaultValue: {
          value: false
        },
        description: 'If `true`, table cells get a compact vertical padding visual treatment',
        name: 'dense',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fixed: {
        defaultValue: {
          value: false
        },
        description: 'Forces all columns to fit in the available viewport space (no scrolling).\n' + 'Only enable this option on tables with a few columns, and the number of columns\n' + 'is static.',
        name: 'fixed',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      uniqueKey: {
        defaultValue: {
          value: ''
        },
        description: '',
        name: 'uniqueKey',
        required: false,
        type: {
          name: 'string'
        }
      },
      groupByKey: {
        defaultValue: null,
        description: '',
        name: 'groupByKey',
        required: false,
        type: {
          name: 'string'
        }
      },
      nestedDataKey: {
        defaultValue: null,
        description: '',
        name: 'nestedDataKey',
        required: false,
        type: {
          name: 'string'
        }
      },
      searchable: {
        defaultValue: null,
        description: '',
        name: 'searchable',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      searchPlaceholder: {
        defaultValue: {
          value: 'Type here to filter results...'
        },
        description: '',
        name: 'searchPlaceholder',
        required: false,
        type: {
          name: 'string'
        }
      },
      orderable: {
        defaultValue: null,
        description: "Display 'Customize Columns' table-level action, which permits re-ordering and hiding columns.",
        name: 'orderable',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      filterableFields: {
        defaultValue: null,
        description: "Array of filterable fields to populate the 'Filter' popover.",
        name: 'filterableFields',
        required: false,
        type: {
          name: 'FilterableField[]'
        }
      },
      initialFilter: {
        defaultValue: null,
        description: "Load an existing Filter object to the 'Filter' popover.\n" + 'This is for display purposes only\n' + 'This filter should already have been applied to the incoming dataset',
        name: 'initialFilter',
        required: false,
        type: {
          name: 'Filter'
        }
      },
      dsOnFilter: {
        defaultValue: null,
        description: 'Emit a Filter object for external datasource to use in fetching data',
        name: 'dsOnFilter',
        required: false,
        type: {
          name: '(args: { filter: Filter; }) => void'
        }
      },
      showFilterChips: {
        defaultValue: null,
        description: 'Must be used with other Filter props. Enables the use of chips to see and remove filter conditions.',
        name: 'showFilterChips',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      sortable: {
        defaultValue: {
          value: true
        },
        description: 'Set to false to disable column-level sorting.',
        name: 'sortable',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      initialSort: {
        defaultValue: null,
        description: 'Load an existing Sort array to the column headers.\n' + 'This is for display purposes only\n' + 'This sort should already have been applied to the incoming dataset',
        name: 'initialSort',
        required: false,
        type: {
          name: 'Sort[]'
        }
      },
      dsOnSort: {
        defaultValue: null,
        description: 'Emit a Sort array for external datasource to use in fetching data',
        name: 'dsOnSort',
        required: false,
        type: {
          name: '(args: { sort: Sort[]; }) => void'
        }
      },
      hidePagination: {
        defaultValue: null,
        description: '',
        name: 'hidePagination',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      loading: {
        defaultValue: null,
        description: '',
        name: 'loading',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      dsOnPage: {
        defaultValue: null,
        description: '',
        name: 'dsOnPage',
        required: false,
        type: {
          name: '(args: { page: number; rowsPerPage: number; }) => void'
        }
      },
      dsIsRowSelectable: {
        defaultValue: null,
        description: '',
        name: 'dsIsRowSelectable',
        required: false,
        type: {
          name: '(args: { row: DataType; children: DataType[]; }) => boolean'
        }
      },
      dsIsCellEditable: {
        defaultValue: null,
        description: '',
        name: 'dsIsCellEditable',
        required: false,
        type: {
          name: '(args: { column: ColumnType; row: DataType; value: ValueType; }) => boolean'
        }
      },
      dsRenderMasterDetail: {
        defaultValue: null,
        description: '',
        name: 'dsRenderMasterDetail',
        required: false,
        type: {
          name: '(args: { row: DataType; }) => ReactNode'
        }
      },
      dsOnEditChange: {
        defaultValue: null,
        description: '',
        name: 'dsOnEditChange',
        required: false,
        type: {
          name: '(args: { row: DataType; rowIndex: number; }) => void'
        }
      },
      dsOnSelectionChange: {
        defaultValue: null,
        description: '',
        name: 'dsOnSelectionChange',
        required: false,
        type: {
          name: '(state: DataType[]) => void'
        }
      },
      dsOnSearchChange: {
        defaultValue: null,
        description: '',
        name: 'dsOnSearchChange',
        required: false,
        type: {
          name: '(state: string) => void'
        }
      },
      dsOnColumnsChange: {
        defaultValue: null,
        description: '',
        name: 'dsOnColumnsChange',
        required: false,
        type: {
          name: '(columns: ColumnType[]) => void'
        }
      },
      dsOnColumnControlPopoverClick: {
        defaultValue: null,
        description: 'Overrides the column control popover button click. This effectively disables "dsOnColumnsChange".',
        name: 'dsOnColumnControlPopoverClick',
        required: false,
        type: {
          name: '() => void'
        }
      },
      maxHeight: {
        defaultValue: null,
        description: '',
        name: 'maxHeight',
        required: false,
        type: {
          name: 'number'
        }
      },
      resizableColumns: {
        defaultValue: {
          value: false
        },
        description: '',
        name: 'resizableColumns',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      rowsPerPage: {
        defaultValue: null,
        description: '',
        name: 'rowsPerPage',
        required: false,
        type: {
          name: 'number'
        }
      },
      rowsPerPageOptions: {
        defaultValue: {
          value: '[5, 10, 15, 20, 30, 50]'
        },
        description: '',
        name: 'rowsPerPageOptions',
        required: false,
        type: {
          name: 'number[]'
        }
      },
      hideTotalRows: {
        defaultValue: null,
        description: '',
        name: 'hideTotalRows',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showSelectAll: {
        defaultValue: {
          value: true
        },
        description: '',
        name: 'showSelectAll',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      delegatedEditableRow: {
        defaultValue: null,
        description: '',
        name: 'delegatedEditableRow',
        required: false,
        type: {
          name: 'DataType'
        }
      },
      dsCustomRowEditAction: {
        defaultValue: null,
        description: '',
        name: 'dsCustomRowEditAction',
        required: false,
        type: {
          name: 'CustomRowEditAction'
        }
      },
      sortStrategy: {
        defaultValue: {
          value: 'client'
        },
        description: '',
        name: 'sortStrategy',
        required: false,
        type: {
          name: 'enum',
          raw: '"server" | "client"',
          value: [{
            value: '"server"'
          }, {
            value: '"client"'
          }]
        }
      }
    }
  };
} catch (e) {}
//# sourceMappingURL=index.js.map